import { useEffect, useState } from "react";
import { X } from "react-bootstrap-icons";
import { SubmitHandler, useForm } from "react-hook-form";
import { TiDelete } from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import GetSvgIcon from "../../assets/images/svgList";
import DragAndDropButton from "../../Components/Buttons/DragAndDropButton";
import FormButton from "../../Components/Buttons/FormButton";
import FormRadioButton from "../../Components/FormTag/FormRadioButton";
import Loader from "../../Components/GlobalLoader/Loader";
import { convertFileToBase64 } from "../../Utility/Utility";
import toast, { Toaster } from "react-hot-toast";
import { URL_VALIDATION } from "../../Shared/Helper/Constant";
import {
  ConfigurationSectionLookUp,
  UomSystem,
} from "./model/accountForm.model";
import {
  attachments,
  CompanyAccConfigUrl,
  CompanyAccountConfiguration,
  LogoType,
  UrlInput,
} from "./model/CompanyAccount.type";

export interface ConfigurationProps {
  handleNext: (data: boolean) => void;
  config?: CompanyAccountConfiguration;
  setConfig?: (data: CompanyAccountConfiguration) => void;
  configLookupData: ConfigurationSectionLookUp;
  isEditScreen?: boolean;
  setFormIsSubmited: (data: boolean) => void;
}

export default function Configuration(props: ConfigurationProps) {
  const {
    config,
    setFormIsSubmited,
    setConfig,
    configLookupData,
    isEditScreen,
  } = props;
  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
  } = useForm<CompanyAccountConfiguration>({ defaultValues: config });
  const navigator = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [urlData, setUrlData] = useState<CompanyAccConfigUrl[]>([]);
  const [unitOfMeasure, setUnitOfMeasure] = useState(config?.uom_system);
  const [data, setData] = useState<File[]>([]);
  const [dataForLogo, setDataForLogo] = useState<LogoType[]>([]);
  const [attachmentFiles, setAttachmentFiles] = useState<any[]>([]);
  const [urlInput, setUrlInput] = useState<UrlInput>({
    urlLink: "",
    urlName: "",
  });

  useEffect(() => {
    if (configLookupData && config) {
      setIsLoading(false);
    }
  }, [configLookupData, config]);

  useEffect(() => {
    if (config?.uom_system) {
      setUnitOfMeasure(config.uom_system);
      setValue("uom_system", config.uom_system);
    }

    // Handle links
    if (config?.links) {
      const formattedData = config.links.map((item: any) => ({
        id: item.id,
        url: item.url,
        description: item.name,
      }));
      setUrlData(formattedData);
    }

    // Handle existing attachments
    if (config?.attachments && config.attachments.length > 0) {
      setAttachmentFiles(config.attachments);
      setData(
        config.attachments.map(
          (attachment: any) =>
            new File([""], attachment.original_name || attachment.description, {
              type: "application/octet-stream",
            })
        )
      );
    }

    // Handle existing logo
    if (config?.logo) {
      setDataForLogo([
        {
          name: config.logo.original_name,
          url: config.logo.url,
          content: config.logo.content,
          type: "application/octet-stream",
        },
      ]);
    }
  }, [config, setValue]);

  const isValidUrl = (url: string): boolean => {
    const urlPattern = new RegExp(URL_VALIDATION);
    return urlPattern.test(url);
  };

  // Handle input changes
  const handleUrlInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUrlInput((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handelAddUrl = () => {
    const { urlLink, urlName } = urlInput;
    const trimmedUrl = urlLink.trim();
    const trimmedTitle = urlName;

    if (!trimmedUrl || !trimmedTitle) {
      toast.error("Please fill in both URL and title fields");
      return;
    }
    if (trimmedUrl.length > 255 || !isValidUrl(trimmedUrl)) {
      toast.error("Please enter a valid URL (e.g., https://www.example.com)");
      return;
    }

    // Add new URL to the list
    const newUrlItem: CompanyAccConfigUrl = {
      url: trimmedUrl,
      description: trimmedTitle,
    };

    // Update urlData state
    setUrlData((prevUrls) => [...prevUrls, newUrlItem]);

    // Clear input fields
    setUrlInput({
      urlLink: "",
      urlName: "",
    });
  };

  const handelRemoveUrl = (idToRemove: number) => {
    const updatedUrlData = urlData.filter((item) => item.id !== idToRemove);
    setUrlData(updatedUrlData);
  };
  const handleUnitOfMeasureKeyDown = (e: any) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      handleUnitOfMeasureClick();
    }
  };

  const handleUnitOfMeasureClick = () => {
    if (config) {
      setUnitOfMeasure(config.uom_system);
    }
  };

  // Update the finalAttachment function
  const finalAttachment = async (dataN: File[]): Promise<attachments[]> => {
    const attachmentsArray = await Promise.all(
      dataN.map(async (file) => {
        if (file.size > 0) {
          const base64Data = await convertFileToBase64(file);
          return {
            original_name: file.name,
            description: file.name,
            content: base64Data,
          };
        }
        return null;
      })
    );

    return attachmentsArray.filter(Boolean) as attachments[];
  };

  const nextData: SubmitHandler<CompanyAccountConfiguration> = async (
    configData
  ) => {
    // Handle attachments
    const newAttachments = data.some((file) => file.size > 0);
    if (newAttachments) {
      const finalAttachments = await finalAttachment(data);
      if (finalAttachments.length > 0) {
        configData.attachments = finalAttachments;
      }
    } else {
      delete configData.attachments;
    }

    // Handle links
    if (urlData && urlData.length > 0) {
      configData.links = urlData;
    } else {
      delete configData.links;
    }

    // Handle logo
    const newLogo = dataForLogo.some(
      (file) => file instanceof File && file.size > 0
    );
    if (newLogo) {
      const logoAttachments = await finalAttachment(
        dataForLogo.filter((file) => file instanceof File) as File[]
      );
      if (logoAttachments.length > 0) {
        configData.logo = {
          original_name: logoAttachments[0].original_name,
          content: logoAttachments[0].content,
        };
      }
    } else {
      delete configData.logo;
    }

    const filteredLinks = configData.links?.map((link: any) => ({
      url: link.url,
      description: link.description,
    }));

    if (setConfig) {
      const formatField = (value: any) => {
        if (value && typeof value === "object" && "code" in value) {
          return value;
        }
        return { code: value };
      };
      setConfig({
        ...configData,
        uom_system: formatField(configData.uom_system),
        links: filteredLinks,
      });
    }

    setFormIsSubmited(true);
  };

  //Fuctionality: To Delete Attachment Files
  const handleDeleteFile = (index: number) => {
    const newData = [...data];
    const newAttachmentFiles = [...attachmentFiles];
    newData.splice(index, 1);
    newAttachmentFiles.splice(index, 1);
    setData(newData);
    setAttachmentFiles(newAttachmentFiles);
  };

  //Functionality: To Delete Logo
  const handleDeleteFileForLogo = () => {
    setDataForLogo([]);
    if (config?.logo) {
      const updatedConfig = { ...config };
      delete updatedConfig.logo;
      setConfig && setConfig(updatedConfig);
    }
  };

  // Update the DragAndDropButton callback for attachments
  const handleAttachmentUpload = (files: File[]) => {
    setData(files);
    setAttachmentFiles([]);
  };

  // Update the DragAndDropButton callback for logo
  const handleLogoUpload = (files: File[]) => {
    setDataForLogo(files);
  };

  const handelCancelBtn = () => {
    navigator("/account/company-account/grid-view");
  };

  // to open attcahment and logo in another tab
  const openFileInNewTab = (
    file: File | { temporaryUrl?: string; content?: string }
  ) => {
    if ("temporaryUrl" in file && file.temporaryUrl) {
      window.open(file.temporaryUrl, "_blank");
    } else if ("content" in file && file.content) {
      window.open(file.content, "_blank");
    } else if (file instanceof File) {
      const url = URL.createObjectURL(file);
      window.open(url, "_blank");
      URL.revokeObjectURL(url);
    }
  };

  return isLoading ? (
    <Loader />
  ) : (
    <div>
      <Toaster />
      <div className="my-2 bg-white rounded p-3">
        <div>
          <div className="d-flex gap-5 px-2">
            <div className="col-4 px-2">
              <label className="fw-medium">Attachment</label>
              <br />
              <div className="d-flex align-items-center mt-1 mb-2">
                <DragAndDropButton
                  buttonName="Upload"
                  title="Import File"
                  buttonClassName="btn btn-color rounded-pill px-5"
                  dragAndDropModalMessage={`Drag and drop the file to import`}
                  acceptableFiles={["PNG", "JPG", "PDF", "DOC", "CSV"]}
                  forCrop={false}
                  multiple={true}
                  data={handleAttachmentUpload}
                />

                <span className="ms-2 fc-grey">
                  {data?.length > 0 ? "" : "No file uploaded"}
                </span>
              </div>
              <div className={data && data.length > 2 ? "scroll-added" : ""}>
                {data &&
                  data.map((file, idx) => (
                    <div
                      key={idx}
                      className="align-self-center align-items-center me-2 mb-2 upload-msg-text fw-medium d-flex clicked-option-with-background-and-cross"
                    >
                      <span
                        className="text-truncate cursor-pointer text-primary"
                        onClick={() =>
                          openFileInNewTab(attachmentFiles[idx] || file)
                        }
                      >
                        {attachmentFiles[idx]?.original_name || file.name}
                      </span>
                      <TiDelete onClick={() => handleDeleteFile(idx)} />
                    </div>
                  ))}
              </div>
            </div>
            <div className="col-4 px-2 ms-5">
              <label className="fw-medium">Account Logo</label>
              <br />
              <div className="d-flex align-items-center mt-1">
                <DragAndDropButton
                  buttonName="Upload"
                  title="Upload Logo"
                  buttonClassName="btn btn-color rounded-pill px-5"
                  dragAndDropModalMessage={`Drag and drop the file to import`}
                  acceptableFiles={["PNG", "JPG"]}
                  forCrop={true}
                  multiple={false}
                  data={handleLogoUpload}
                />
                <span className="ms-2 fc-grey">
                  {config?.logo?.url || dataForLogo?.length > 0
                    ? ""
                    : "No file uploaded"}
                </span>
              </div>
              {dataForLogo && dataForLogo.length > 0 && (
                <div className="mt-2 position-relative w-fit">
                  {"url" in dataForLogo[0] && dataForLogo[0].url ? (
                    <>
                      <img
                        src={dataForLogo[0].url}
                        alt="Company Logo"
                        className="img-fluid cursor-pointer"
                        style={{ maxHeight: "100px", objectFit: "contain" }}
                        onClick={() =>
                          dataForLogo[0].url &&
                          openFileInNewTab({
                            temporaryUrl: dataForLogo[0].url,
                          })
                        }
                      />
                      <TiDelete
                        className="position-absolute delete-attachment-icon cursor-pointer"
                        onClick={handleDeleteFileForLogo}
                      />
                    </>
                  ) : (
                    <>
                      <span
                        className="align-self-center align-items-center me-2 mb-2 upload-msg-text fw-medium d-flex clicked-option-with-background-and-cross me-2"
                      
                    
                      >
                         <span
                        className="text-truncate cursor-pointer text-primary "
                        onClick={() => openFileInNewTab(dataForLogo[0])}
                      >
                         {dataForLogo[0] instanceof File
                          ? dataForLogo[0].name
                          : dataForLogo[0].name}

                      </span>
                       
                        {dataForLogo[0] && dataForLogo[0].name && (
                          <TiDelete
                            
                            onClick={handleDeleteFileForLogo}
                            style={{ background: "white", borderRadius: "50%" }}
                          />
                        )}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="url-input-div mt-4 px-2">
            <div className="col-12">
              <label className="url-label fw-medium">URL</label>
            </div>
            <div className="d-flex mt-1">
              <div className="col-6">
                <input
                  className="form-control py-2"
                  type="text"
                  name="urlLink"
                  placeholder="Enter URL"
                  value={urlInput.urlLink}
                  onChange={handleUrlInputChange}
                />
              </div>
              <div className="col-4">
                <input
                  className="form-control border-start-0 py-2"
                  type="text"
                  name="urlName"
                  placeholder="Title"
                  value={urlInput.urlName}
                  onChange={handleUrlInputChange}
                />
              </div>
              <div className="col-2 ms-2">
                <button
                  className="btn btn-color py-2 url-plus-btn"
                  onClick={handelAddUrl}
                >
                  {GetSvgIcon.URLAddBtn}
                </button>
              </div>
            </div>
            <div>
              {urlData &&
                urlData.map((item, index) => (
                  <div className="d-flex mt-1" key={index}>
                    <div className="col-10 d-flex">
                      <div>
                        <a href="" className="nav-link text-primary">
                          {item.description}
                        </a>
                      </div>
                      <div
                        className="text-danger ms-auto cursor-pointer"
                        onClick={() => item.id && handelRemoveUrl(item.id)}
                      >
                        {<X />}
                      </div>
                    </div>
                    <div className="col-2"></div>
                  </div>
                ))}
            </div>
          </div>
          <div className="configuration-radio-area d-flex gap-5 border-top mt-5 px-2">
            <div className="col-4 mt-3 d-flex me-5">
              <div className="col-7">
                <div>
                  <label className="fw-medium">Unit of Measure *</label>
                </div>
                {errors?.uom_system && (
                  <div>
                    <span className="text-danger">
                      {errors.uom_system?.message?.toString()}
                    </span>
                  </div>
                )}
              </div>
              <div className="col-5">
                <div className="d-flex ">
                  {configLookupData?.uom_system.map(
                    (item: UomSystem, index: number) => {
                      return (
                        <>
                          <FormRadioButton
                            className="form-check col-6 me-3"
                            radioId={`sstRadio${index}`}
                            required={true}
                            name="uom_system"
                            register={register}
                            errors={errors}
                            labelName={item.description}
                            onClick={handleUnitOfMeasureClick}
                            value={item.code}
                            onKeyDown={handleUnitOfMeasureKeyDown}
                            isLabelClass={true}
                            labelClass="form-check-label"
                            defaultChecked={unitOfMeasure}
                          />
                        </>
                      );
                    }
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="my-2 bg-white rounded py-3">
        <div className="d-flex flex-row-reverse">
          <FormButton
            // tabIndex={10}
            className="sph-btn me-4"
            handleClick={handleSubmit(nextData)}
          >
            {isEditScreen ? "Update" : "Submit"}
          </FormButton>
          <FormButton
            // tabIndex={11}
            className="sph-btn sph-shift me-4"
            handleClick={handelCancelBtn}
          >
            Cancel
          </FormButton>
        </div>
      </div>
    </div>
  );
}
